import QbCardCard from '../QBCardCard/QBCardCard';
import React, {useEffect, useState} from 'react';
import RegistrationCard from '../../package/RegistrationCard/RegistrationCard';
import InfoCard from '../InfoCard/InfoCard';
import {useTranslation} from 'react-i18next';
import i18nNamespaces from '../../../const/i18nNamespaces';
import {QbCardOwner} from '../../../types/individualPackages';
import PriceCard from '../PriceCard/PriceCard';
import {useAppDispatch, useAppSelector} from '../../../hooks/customReduxHooks';
import {clearError, selectAuthUserData} from '../../../store/auth';
import {PATIENT, PHONE_NUMBER_CONFIRMATION} from '../../../const/auth';
import PhoneConfirmationCard from '../../package/PhoneConfirmationCard/PhoneConfirmationCard';
import PurchaseCard from '../PurchaseCard/PurchaseCard';
import LoginCard from '../../package/LoginCard/LoginCard';

enum FormStageEnum {
    QbCardAuthentication = 'QbCardAuthentication',
    SimpleRegistration = 'SimpleRegistration',
    SMSConfirmation = 'SMSConfirmation',
    Purchase = 'Purchase',
    Login = 'Login',
}

type UniformPackageProps = {
    individualPackageDraftId?: number;
    qbServiceId: string;
    price?: number;
};

const initialVisibleCardsState = {
    qbCard: false,
    infoCard: false,
    priceCard: false,
    phoneConfirmation: false,
    registration: false,
    purchase: false,
    login: false,
};

const formStage = {
    [FormStageEnum.QbCardAuthentication]: {
        ...initialVisibleCardsState,
        qbCard: true,
    },
    [FormStageEnum.SimpleRegistration]: {
        ...initialVisibleCardsState,
        infoCard: true,
        priceCard: true,
        registration: true,
    },
    [FormStageEnum.Login]: {
        ...initialVisibleCardsState,
        infoCard: true,
        priceCard: true,
        login: true,
    },
    [FormStageEnum.SMSConfirmation]: {
        ...initialVisibleCardsState,
        infoCard: true,
        priceCard: true,
        phoneConfirmation: true,
    },
    [FormStageEnum.Purchase]: {
        ...initialVisibleCardsState,
        infoCard: true,
        priceCard: true,
        purchase: true,
    },
};
const UniformPackage = ({
    individualPackageDraftId,
    qbServiceId,
    price,
}: UniformPackageProps) => {
    const {t} = useTranslation(i18nNamespaces.INDIVIDUAL_PACKAGES);
    const [qbCardOwner, setQbCardOwner] = useState<QbCardOwner | null>(null);
    const authUserData = useAppSelector(selectAuthUserData);
    const [visibleCards, setVisibleCards] = useState(initialVisibleCardsState);
    const dispatch = useAppDispatch();

    useEffect(() => {
        setVisibleCards(formStage[FormStageEnum.QbCardAuthentication]);
    }, []);

    useEffect(() => {
        if (qbCardOwner && authUserData?.role === PHONE_NUMBER_CONFIRMATION) {
            return setVisibleCards(formStage[FormStageEnum.SMSConfirmation]);
        }
        if (qbCardOwner && authUserData?.role === PATIENT) {
            return setVisibleCards(formStage[FormStageEnum.Purchase]);
        }
        if (qbCardOwner) {
            return setVisibleCards(formStage[FormStageEnum.SimpleRegistration]);
        }

        return setVisibleCards(formStage[FormStageEnum.QbCardAuthentication]);
    }, [qbCardOwner, authUserData]);

    const handleLoginClick = () => {
        dispatch(clearError());
        setVisibleCards(formStage[FormStageEnum.Login]);
    };

    const handleRegisterClick = () => {
        dispatch(clearError());
        setVisibleCards(formStage[FormStageEnum.SimpleRegistration]);
    };

    return (
        <div>
            {visibleCards.qbCard && (
                <QbCardCard
                    onSuccess={setQbCardOwner}
                    qbServiceId={qbServiceId}
                />
            )}
            {visibleCards.infoCard && (
                <InfoCard title={t('uniformCard')}>
                    {qbCardOwner.cardNumber}
                    <br />
                    {qbCardOwner.name} {qbCardOwner.surname}
                </InfoCard>
            )}
            {visibleCards.priceCard && <PriceCard price={price} />}
            {visibleCards.phoneConfirmation && <PhoneConfirmationCard />}
            {visibleCards.registration && (
                <RegistrationCard
                    onLoginClick={handleLoginClick}
                    ownerEmail={qbCardOwner.email}
                />
            )}
            {visibleCards.login && (
                <LoginCard onRegisterClick={handleRegisterClick} />
            )}
            {visibleCards.purchase && (
                <PurchaseCard
                    individualPackageDraftId={individualPackageDraftId}
                    qbCardOwner={qbCardOwner}
                />
            )}
        </div>
    );
};

export default UniformPackage;
